import {
  MutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  InstantReflex,
  InstantReflexVersionRef,
  ReflexFeedbackRating,
  Note,
  NoteRevision,
  SiteShift,
  Attachment,
} from 'shiftNotes/_types';
import { APIKey, apiRoutes } from 'constants/apiKeys';
import dayjs from 'dayjs';
import baseFetcher from 'shared/helpers/baseFetcher';

export function useShiftNoteRevisions(noteId?: string) {
  return useQuery<NoteRevision[]>({
    queryKey: [APIKey.SHIFT_NOTE_REVISIONS, noteId],
    queryFn: () => baseFetcher(apiRoutes.shiftNoteRevisions(noteId)),
    enabled: !!noteId,
  });
}

export function useShiftNote(
  id?: string,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((query: { state: { data: Note | undefined } }) => number | false);
  }
) {
  return useQuery<Note>({
    queryKey: [APIKey.SHIFT_NOTE, id],
    queryFn: () => baseFetcher(apiRoutes.shiftNote(id)),
    enabled: (options?.enabled ?? true) && !!id,
    refetchInterval: options?.refetchInterval,
  });
}

function useUpdateInstantReflex() {
  const queryClient = useQueryClient();
  return (noteId: string, data: InstantReflex) => {
    queryClient.setQueryData<Note | undefined>(
      [APIKey.SHIFT_NOTE, noteId],
      (oldData) => (oldData ? { ...oldData, instant_reflex: data } : oldData)
    );
  };
}

export type AddShiftNoteData =
  | FormData
  | { text_note: string; resolve?: boolean };

type UseAddShiftNoteMutationOptions = Pick<
  MutationOptions<Note[], Error, AddShiftNoteData>,
  'onSuccess'
>;

export function useAddShiftNoteMutation({
  onSuccess,
  eventId,
}: UseAddShiftNoteMutationOptions & { eventId?: string }) {
  const queryClient = useQueryClient();
  return useMutation<Note[], Error, AddShiftNoteData>({
    mutationFn: (data) => {
      const url = eventId
        ? `${apiRoutes.shiftNotes()}?event_id=${encodeURIComponent(eventId)}`
        : apiRoutes.shiftNotes();
      return baseFetcher(url, {
        method: 'POST',
        data,
        removeHeaders: 'text_note' in data ? [] : ['Content-Type'],
      });
    },
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [APIKey.USER] });
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
      queryClient.invalidateQueries({ queryKey: [APIKey.GUIDANCE_QUESTIONS] });
      onSuccess?.(...args);
    },
  });
}

type UpdateShiftNoteMutationVariables = {
  id: string;
  data: Partial<{
    edited_text: Note['edited_text'];
    is_private: Note['is_private'];
    // PATCH expects department IDs, not department objects.
    departments: string[];
  }>;
  clearEntities?: boolean;
};

export function useUpdateShiftNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation<Note, unknown, UpdateShiftNoteMutationVariables>({
    mutationFn: ({ id, data }) =>
      baseFetcher(apiRoutes.shiftNote(id), {
        method: 'PATCH',
        data,
      }),
    onSuccess: (updatedNote, { id, data, clearEntities }) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { departments: _departmentIds, ...rest } = data;
      const patch: Partial<Note> = { ...rest };
      if (clearEntities) {
        patch.entity_extractions = [];
        patch.partial_extracted_at = null;
        patch.detailed_extracted_at = null;
      }

      const touchesInsights =
        clearEntities ||
        Object.prototype.hasOwnProperty.call(data, 'edited_text');

      queryClient.setQueryData<Note | undefined>(
        [APIKey.SHIFT_NOTE, id],
        (oldNote) => {
          const next = oldNote
            ? ({ ...oldNote, ...updatedNote, ...patch } as Note)
            : ({ ...updatedNote, ...patch } as Note);

          if (!touchesInsights && oldNote) {
            if (
              oldNote.instant_reflex &&
              (next.instant_reflex === null ||
                next.instant_reflex === undefined)
            ) {
              next.instant_reflex = oldNote.instant_reflex;
            }
            if (oldNote.partial_extracted_at && !next.partial_extracted_at) {
              next.partial_extracted_at = oldNote.partial_extracted_at;
            }
            if (
              (oldNote.entity_extractions?.length ?? 0) > 0 &&
              Array.isArray(next.entity_extractions) &&
              next.entity_extractions.length === 0
            ) {
              next.entity_extractions = oldNote.entity_extractions;
            }
          }

          return next;
        }
      );

      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
      queryClient.invalidateQueries({ queryKey: [APIKey.GUIDANCE_QUESTIONS] });
    },
  });
}

export function useDeleteShiftNoteMutation() {
  const queryClient = useQueryClient();
  return useMutation<Note, unknown, string>({
    mutationFn: (id) =>
      baseFetcher(apiRoutes.shiftNote(id), {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.USER] });
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
      queryClient.invalidateQueries({ queryKey: [APIKey.GUIDANCE_QUESTIONS] });
    },
  });
}

export function useUnlinkShiftNoteFromEventMutation() {
  const queryClient = useQueryClient();
  return useMutation<Note | null, unknown, string>({
    mutationFn: (id) =>
      baseFetcher(apiRoutes.unlinkShiftNoteFromEvent(id), {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
      queryClient.invalidateQueries({ queryKey: [APIKey.GUIDANCE_QUESTIONS] });
    },
  });
}

const FALSE_ALARM_AFFECTED_KEYS = [
  APIKey.EVENT,
  APIKey.SITE_SHIFTS,
  APIKey.OVERVIEW_NUMBERS,
  APIKey.OVERVIEW_URGENCY,
  APIKey.OVERVIEW_RISKS,
  APIKey.URGENCY_STATISTICS,
  APIKey.RISK_STATISTICS,
  APIKey.SIGNAL_STATISTICS,
  APIKey.NOTE_HEATMAP,
  APIKey.INSIGHTS_SUMMARY,
  APIKey.INSIGHTS_EVENTS,
  APIKey.INSIGHTS_EVENT,
  APIKey.PROFILES,
];

export function useMarkShiftNoteFalseAlarmMutation() {
  const queryClient = useQueryClient();
  return useMutation<Note, unknown, string>({
    mutationFn: (id) =>
      baseFetcher(apiRoutes.markShiftNoteFalseAlarm(id), {
        method: 'POST',
      }),
    onSuccess: (updatedNote, id) => {
      queryClient.setQueryData([APIKey.SHIFT_NOTE, id], updatedNote);
      FALSE_ALARM_AFFECTED_KEYS.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      );
    },
  });
}

export function useSiteShifts(date: Date | null) {
  return useQuery<SiteShift[]>({
    queryKey: [APIKey.SITE_SHIFTS, date],
    queryFn: () =>
      baseFetcher(
        date
          ? `${apiRoutes.siteShifts()}?date=${dayjs(date).format('YYYY-MM-DD')}`
          : apiRoutes.siteShifts()
      ),
  });
}

export function useSiteShift(id: string) {
  return useQuery<SiteShift>({
    queryKey: [APIKey.SITE_SHIFTS, id],
    queryFn: () => baseFetcher(apiRoutes.siteShift(id)),
    enabled: !!id,
  });
}

export function useInstantReflex(id: string, enabled: boolean) {
  return useQuery<InstantReflex>({
    queryKey: [APIKey.INSTANT_REFLEXES, id],
    queryFn: () => baseFetcher(apiRoutes.instantReflex(id)),
    enabled: !!id && enabled,
  });
}

export type ReflexQuestionAnswer = {
  id: string;
  answer_text?: string | null;
  answer_boolean?: boolean | null;
  answer_date?: string | null;
};

type SubmitReflexQuestionAnswersVariables = {
  stepId: string;
  reflexQuestions: ReflexQuestionAnswer[];
};

export function useSubmitReflexQuestionAnswersMutation() {
  const queryClient = useQueryClient();
  return useMutation<Note, unknown, SubmitReflexQuestionAnswersVariables>({
    mutationFn: ({ stepId, reflexQuestions }) =>
      baseFetcher(apiRoutes.reflexStepAnswer(stepId), {
        method: 'POST',
        data: { reflex_questions: reflexQuestions },
      }),
    onSuccess: (updatedNote) => {
      queryClient.setQueryData<Note | undefined>(
        [APIKey.SHIFT_NOTE, updatedNote.id],
        updatedNote
      );
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
    },
  });
}

export type ReflexFeedbackItem = {
  step_id: string;
  rating: ReflexFeedbackRating;
  comment?: string | null;
};

export type ReflexFeedbackSection = 'reflex' | 'missing_info';

export type SubmitReflexFeedbackVariables = {
  reflexId: string;
  noteId: string;
  section: ReflexFeedbackSection;
  overallComment: string | null;
  items: ReflexFeedbackItem[];
};

export type ReflexFeedbackResponse = {
  regenerated: boolean;
  instant_reflex: InstantReflex;
  versions: InstantReflex[];
};

type ReflexFeedbackStatus = 'liked' | 'disliked';

type ReflexFeedbackStepPayload = {
  step_id: string;
  feedback_status: ReflexFeedbackStatus;
  feedback?: string;
};

function toFeedbackStepPayload({
  step_id: stepId,
  rating,
  comment,
}: ReflexFeedbackItem): ReflexFeedbackStepPayload {
  return {
    step_id: stepId,
    feedback_status: rating === 'positive' ? 'liked' : 'disliked',
    ...(rating === 'negative' && comment ? { feedback: comment } : {}),
  };
}

export const SUBMIT_REFLEX_FEEDBACK_MUTATION_KEY = [
  APIKey.INSTANT_REFLEXES,
  'feedback',
];

export function useSubmitReflexFeedbackMutation() {
  const updateInstantReflex = useUpdateInstantReflex();
  const queryClient = useQueryClient();
  return useMutation<
    ReflexFeedbackResponse,
    unknown,
    SubmitReflexFeedbackVariables
  >({
    mutationKey: SUBMIT_REFLEX_FEEDBACK_MUTATION_KEY,
    mutationFn: async ({ reflexId, overallComment, items }) => {
      const versions: InstantReflex[] = await baseFetcher(
        apiRoutes.instantReflexFeedback(reflexId),
        {
          method: 'POST',
          data: {
            feedback: overallComment,
            steps: items.map(toFeedbackStepPayload),
          },
        }
      );
      const [current] = versions;
      const versionRefs: InstantReflexVersionRef[] = versions.map((reflex) => ({
        id: reflex.id,
        version: reflex.version ?? 1,
        created: reflex.created,
      }));
      return {
        regenerated: current.id !== reflexId,
        instant_reflex: { ...current, versions: versionRefs },
        versions,
      };
    },
    onSuccess: (data, { noteId }) => {
      updateInstantReflex(noteId, data.instant_reflex);
      data.versions.forEach((reflex) => {
        queryClient.setQueryData<InstantReflex>(
          [APIKey.INSTANT_REFLEXES, reflex.id],
          reflex
        );
      });
      if (data.regenerated) {
        queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
      }
    },
  });
}

export const GENERATE_INSTANT_REFLEX_MUTATION_KEY = [
  APIKey.INSTANT_REFLEXES,
  'generate',
];

export function useGenerateInstantReflexMutation() {
  const updateInstantReflex = useUpdateInstantReflex();
  const queryClient = useQueryClient();
  return useMutation<InstantReflex, unknown, string>({
    mutationKey: GENERATE_INSTANT_REFLEX_MUTATION_KEY,
    mutationFn: (noteId) =>
      baseFetcher(apiRoutes.generateInstantReflex(noteId), {
        method: 'POST',
      }),
    onSuccess: (data, noteId) => {
      updateInstantReflex(noteId, data);
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
    },
  });
}

type UseExtractEntitiesMutationOptions = Pick<
  MutationOptions<Note, Error, string>,
  'onSuccess'
>;

export const EXTRACT_ENTITIES_MUTATION_KEY = [
  APIKey.SHIFT_NOTE,
  'extract-entities',
];

export function useExtractEntitiesMutation({
  onSuccess,
}: UseExtractEntitiesMutationOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation<Note, unknown, string>({
    mutationKey: EXTRACT_ENTITIES_MUTATION_KEY,
    mutationFn: (noteId) =>
      baseFetcher(apiRoutes.extractEntities(noteId), {
        method: 'POST',
      }),
    onSuccess: (data, noteId, ...args) => {
      queryClient.setQueryData<Note | undefined>(
        [APIKey.SHIFT_NOTE, noteId],
        data
      );
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
      onSuccess?.(data, noteId, ...args);
    },
  });
}

export function useAddShiftNoteAttachmentMutation(noteId: string) {
  const queryClient = useQueryClient();
  return useMutation<Attachment, unknown, FormData>({
    mutationFn: (data) =>
      baseFetcher(apiRoutes.shiftNoteAttachments(noteId), {
        method: 'POST',
        data,
        removeHeaders: ['Content-Type'],
      }),
    onSuccess: (data) => {
      queryClient.setQueryData<Note | undefined>(
        [APIKey.SHIFT_NOTE, noteId],
        (oldData) =>
          oldData
            ? { ...oldData, attachments: [...oldData.attachments, data] }
            : oldData
      );
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
    },
  });
}

export function useDeleteShiftNoteAttachmentMutation(noteId: string) {
  const queryClient = useQueryClient();
  return useMutation<Attachment, unknown, string>({
    mutationFn: (id) =>
      baseFetcher(apiRoutes.shiftNoteAttachment(id), {
        method: 'DELETE',
      }),
    onSuccess: (_, id) => {
      queryClient.setQueryData<Note | undefined>(
        [APIKey.SHIFT_NOTE, noteId],
        (oldData) =>
          oldData
            ? {
                ...oldData,
                attachments: oldData.attachments.filter(
                  (attachment) => attachment.id !== id
                ),
              }
            : oldData
      );
      queryClient.invalidateQueries({ queryKey: [APIKey.EVENT] });
    },
  });
}
